import React, { useEffect, useRef, useState } from 'react'
import { Button, Icon, Spin, message as antMessage } from 'antd'

import FinalizedReportModal from './FinalizedReportModal'
import { MAX_REFERENCE_FILE_SIZE } from './constants'
import {
  ConversationCard,
  MessageList,
  ProgressPreview,
  StoryboardPlanPreview,
  VisionDirectionCard
} from './components'
import { ImagePreview } from '../../../../../../components'
import {
  formatCount,
  getFriendlyErrorMessage,
  getGeneratedAssets,
  getPhaseIndex,
  getPhaseCount,
  getNextPhaseLabel,
  getPhaseView,
  getReferenceKind,
  getStoryboardPlanCount,
  isAcceptedReferenceFile,
  revokePendingReferencePreviews
} from './utils'
import './CreativeDirectionChat.scss'
import './CreativeDirectionChatRedesign.scss'

const getMessagePhase = message => message?.phase || 'vision'
const ACTIVE_CHAT_GENERATION_STATUSES = [
  'pending',
  'queued',
  'processing',
  'running',
  'in_progress'
]
const getAssetThumbnailUrl = asset =>
  asset?.thumbnail_url ||
  asset?.thumbnailUrl ||
  asset?.image_url ||
  asset?.imageUrl ||
  ''
const getAssetFullImageUrl = asset =>
  // asset?.image_url ||
  // asset?.imageUrl ||
  asset?.thumbnail_url ||
  asset?.thumbnailUrl ||
  ''

const ENTITY_KEY_FIELDS_BY_PHASE = {
  characters: ['character_key', 'characterKey'],
  outfits: ['outfit_key', 'outfitKey'],
  locations: ['location_key', 'locationKey']
}

const ASSET_TYPE_LABELS = {
  character_sheet: 'Character Sheet',
  emotion_sheet: 'Emotion Sheet',
  outfit_sheet: 'Outfit Sheet',
  location_sheet: 'Location Sheet',
  turnaround: 'Turnaround',
  reference: 'Reference',
  storyboard: 'Storyboard'
}

const normalizeLookupKey = value =>
  value === undefined || value === null ? '' : String(value).trim().toLowerCase()

const formatAssetTypeLabel = value => {
  const type = String(value || '').trim()
  if (!type) return 'Generated Visual'

  const normalizedType = type.toLowerCase()
  if (ASSET_TYPE_LABELS[normalizedType]) {
    return ASSET_TYPE_LABELS[normalizedType]
  }

  return normalizedType
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(part => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ')
}

const getEntityDisplayName = entity =>
  entity?.name ||
  entity?.label ||
  entity?.title ||
  ''

const getAssetEntityDisplayName = asset =>
  asset?.parent_entity?.name ||
  asset?.parentEntity?.name ||
  asset?.entity?.name ||
  asset?.parent_entity_name ||
  asset?.parentEntityName ||
  asset?.entity_name ||
  asset?.entityName ||
  ''

const buildEntityLookup = (phaseKey, entities = []) => {
  const lookup = {}
  const phaseKeyFields = ENTITY_KEY_FIELDS_BY_PHASE[phaseKey] || []

  entities.forEach(entity => {
    const keys = [
      entity?.id,
      entity?.key,
      entity?.uuid,
      entity?.slug,
      entity?.entity_id,
      entity?.entityId,
      entity?.entity_key,
      entity?.entityKey,
      entity?.db_id,
      entity?.dbId,
      entity?.placeholder,
      entity?.name,
      ...phaseKeyFields.map(field => entity?.[field])
    ]

    keys.forEach(key => {
      const normalizedKey = normalizeLookupKey(key)
      if (normalizedKey) lookup[normalizedKey] = entity
    })
  })

  return lookup
}

const getAssetEntity = (asset, entityLookup) => {
  const lookupKeys = [
    asset?.parent_entity_id,
    asset?.parentEntityId,
    asset?.parent_entity_key,
    asset?.parentEntityKey,
    asset?.entity_id,
    asset?.entityId,
    asset?.entity_key,
    asset?.entityKey,
    asset?.key,
    asset?.uuid,
    asset?.parent_entity_name,
    asset?.parentEntityName,
    asset?.entity_name,
    asset?.entityName,
    asset?.character_key,
    asset?.characterKey,
    asset?.outfit_key,
    asset?.outfitKey,
    asset?.location_key,
    asset?.locationKey,
    asset?.name
  ]

  for (const key of lookupKeys) {
    const normalizedKey = normalizeLookupKey(key)
    if (normalizedKey && entityLookup[normalizedKey]) {
      return entityLookup[normalizedKey]
    }
  }

  return null
}

const buildVisualAssetViewModel = (asset, index, entityLookup) => {
  const thumbnailUrl = getAssetThumbnailUrl(asset)
  const fullImageUrl = getAssetFullImageUrl(asset)
  const entity = getAssetEntity(asset, entityLookup)
  const entityName =
    getEntityDisplayName(entity) || getAssetEntityDisplayName(asset)
  const assetTypeLabel = formatAssetTypeLabel(
    asset?.asset_type || asset?.assetType
  )
  const hasEntityName = Boolean(entityName)

  return {
    asset,
    index,
    thumbnailUrl,
    fullImageUrl,
    entityName,
    hasEntityName,
    assetTypeLabel,
    label: hasEntityName ? `${entityName} - ${assetTypeLabel}` : assetTypeLabel
  }
}

const groupVisualAssetsByType = visualAssets =>
  visualAssets.reduce((groups, item) => {
    const key = item.assetTypeLabel || 'Generated Visual'
    const existingGroup = groups.find(group => group.key === key)

    if (existingGroup) {
      existingGroup.items.push(item)
      return groups
    }

    return [
      ...groups,
      {
        key,
        label: key,
        items: [item]
      }
    ]
  }, [])

const VISUAL_GENERATION_ACTION_NAMES = [
  'generate_images',
  'generate_storyboard',
  'regenerate_image',
  'edit_image',
  'edit_image_with_reference',
  'generation_started',
  'generation_queued'
]

const getMessageActionName = action =>
  String(
    action?.name ||
    action?.tool_name ||
    action?.toolName ||
    action?.tool ||
    action?.action ||
    action?.event_name ||
    action?.eventName ||
    action?.type ||
    action?.event ||
    ''
  )
    .trim()
    .toLowerCase()

const getMessageActionResult = action => {
  if (!action || typeof action !== 'object') return {}
  if (action.result && typeof action.result === 'object') return action.result
  if (action.output && typeof action.output === 'object') return action.output
  if (action.data && typeof action.data === 'object') return action.data
  return action
}

const getMessageActionTaskId = action => {
  const result = getMessageActionResult(action)

  return (
    result.task_id ||
    result.taskId ||
    action?.task_id ||
    action?.taskId ||
    result.output?.task_id ||
    result.output?.taskId ||
    ''
  )
}

const isNothingToGenerateEvent = action => {
  const actionName = getMessageActionName(action)
  const result = getMessageActionResult(action)

  return (
    actionName === 'generation_started' &&
    result.queued === false &&
    (result.nothing_to_generate === true ||
      result.nothingToGenerate === true) &&
    !getMessageActionTaskId(action)
  )
}

const isVisualGenerationAction = action => {
  if (isNothingToGenerateEvent(action)) return false

  const actionName = getMessageActionName(action)
  const result = getMessageActionResult(action)
  const hasGenerationName = VISUAL_GENERATION_ACTION_NAMES.includes(actionName)
  const hasGenerationText =
    (actionName.includes('generate') || actionName.includes('generation')) &&
    (actionName.includes('image') ||
      actionName.includes('visual') ||
      actionName.includes('storyboard') ||
      actionName.includes('started') ||
      actionName.includes('queued'))

  return (
    hasGenerationName ||
    hasGenerationText ||
    Boolean(
      getMessageActionTaskId(action) &&
      (result.generated ||
        result.assets ||
        result.visuals ||
        result.generation ||
        result.queued)
    )
  )
}

const CreativeDirectionChat = ({
  sessionId,
  messages,
  metadata,
  phaseSteps,
  phaseProgress,
  phaseData,
  creativeGuideline,
  progressPercent,
  creativeStatement,
  acceptedExtensions,
  isCreatingSession,
  isStreaming,
  isSynthesizingVision,
  isUploadingReference,
  referenceUploadProgress,
  referenceUploadStatus,
  referenceUploadMessage,
  isGenerationPolling,
  generationStatus,
  isLockingVision,
  isVisionLocked,
  isFinalized,
  phaseBusyAction,
  error,
  onStopAgentTurn,
  onSendMessage,
  onUploadRequestedReference,
  onSynthesizeVision,
  onLockVision,
  onFinalizeCreativeDirection,
  onStartPrompt,
  onRetryCreateSession
}) => {
  const [draftMessage, setDraftMessage] = useState('')
  const [pendingReferences, setPendingReferences] = useState([])
  const [isFinalizedReportOpen, setIsFinalizedReportOpen] = useState(false)
  const [isProgressPanelCollapsed, setIsProgressPanelCollapsed] = useState(
    false
  )
  const [headerPhase, setHeaderPhase] = useState(metadata.currentPhase || 'vision')
  const scrollRef = useRef(null)
  const scrollAnchorRef = useRef(null)
  const pendingReferencesRef = useRef([])
  const currentPhase = metadata.currentPhase || 'vision'
  const isVisionPhase = currentPhase === 'vision'

  const handleToggleProgressPanel = () => {
    setIsProgressPanelCollapsed(prevValue => !prevValue)
  }

  const handlePhaseSelect = phaseKey => {
    setHeaderPhase(phaseKey)

    const phaseSection = document.getElementById(
      `creative-direction-phase-${phaseKey}`
    )

    if (phaseSection) {
      phaseSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    } else if (phaseKey === 'vision' && scrollRef.current) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }
  }

  useEffect(() => {
    pendingReferencesRef.current = pendingReferences
  }, [pendingReferences])

  useEffect(() => {
    setHeaderPhase(currentPhase)
  }, [currentPhase])

  useEffect(() => {
    let frameId = null

    const updateHeaderPhaseFromViewport = () => {
      const container = scrollRef.current
      if (!container || !phaseSteps.length) return

      const conversationHeader = container.previousElementSibling
      const headerBottom = conversationHeader
        ? conversationHeader.getBoundingClientRect().bottom
        : 0
      const trackingLine = Math.max(0, headerBottom) + 16
      const phaseMarkers = phaseSteps
        .map(step => {
          const element = document.getElementById(
            `creative-direction-phase-${step.key}`
          )

          if (!element) return null

          return {
            key: step.key,
            top: element.getBoundingClientRect().top
          }
        })
        .filter(Boolean)

      if (!phaseMarkers.length) return

      const reachedMarkers = phaseMarkers.filter(
        item => item.top <= trackingLine
      )
      const activeMarker = reachedMarkers.length
        ? reachedMarkers[reachedMarkers.length - 1]
        : phaseMarkers[0]

      if (activeMarker?.key) {
        setHeaderPhase(prevValue =>
          prevValue === activeMarker.key ? prevValue : activeMarker.key
        )
      }
    }

    const scheduleHeaderPhaseUpdate = () => {
      if (frameId !== null) return

      frameId = window.requestAnimationFrame(() => {
        frameId = null
        updateHeaderPhaseFromViewport()
      })
    }

    const container = scrollRef.current
    if (!container) return undefined

    scheduleHeaderPhaseUpdate()
    container.addEventListener('scroll', scheduleHeaderPhaseUpdate, {
      passive: true
    })
    window.addEventListener('scroll', scheduleHeaderPhaseUpdate, {
      passive: true
    })
    document.addEventListener('scroll', scheduleHeaderPhaseUpdate, true)
    window.addEventListener('resize', scheduleHeaderPhaseUpdate)

    return () => {
      container.removeEventListener('scroll', scheduleHeaderPhaseUpdate)
      window.removeEventListener('scroll', scheduleHeaderPhaseUpdate)
      document.removeEventListener('scroll', scheduleHeaderPhaseUpdate, true)
      window.removeEventListener('resize', scheduleHeaderPhaseUpdate)

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId)
      }
    }
  }, [phaseSteps, messages.length])

  useEffect(
    () => () => {
      revokePendingReferencePreviews(pendingReferencesRef.current)
    },
    []
  )

  useEffect(() => {
    const scrollToBottom = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight
      }

      if (scrollAnchorRef.current?.scrollIntoView) {
        scrollAnchorRef.current.scrollIntoView({
          block: 'end',
          inline: 'nearest'
        })
      }
    }

    const frameId = window.requestAnimationFrame(scrollToBottom)
    const timeoutId = window.setTimeout(scrollToBottom, 120)
    const lateTimeoutId = window.setTimeout(scrollToBottom, 320)

    return () => {
      window.cancelAnimationFrame(frameId)
      window.clearTimeout(timeoutId)
      window.clearTimeout(lateTimeoutId)
    }
  }, [
    creativeStatement,
    currentPhase,
    isStreaming,
    isSynthesizingVision,
    messages,
    phaseData,
    phaseBusyAction,
    error
  ])

  const handleAttachReference = file => {
    if (!file) return false

    if (!isAcceptedReferenceFile(file, acceptedExtensions)) {
      antMessage.error('Attach an image or video reference file.')
      return false
    }

    if (file.size > MAX_REFERENCE_FILE_SIZE) {
      antMessage.error('Reference files must be 50 MB or less.')
      return false
    }

    const fileKey =
      file.uid || `${file.name}-${file.size}-${file.lastModified || Date.now()}`
    const previewUrl =
      getReferenceKind(file) === 'image' && window.URL?.createObjectURL
        ? window.URL.createObjectURL(file)
        : ''

    setPendingReferences(prev => {
      if (prev.some(item => item.fileKey === fileKey)) {
        if (previewUrl && window.URL?.revokeObjectURL) {
          window.URL.revokeObjectURL(previewUrl)
        }
        return prev
      }

      return [
        ...prev,
        {
          id: `reference-${fileKey}`,
          fileKey,
          file,
          previewUrl
        }
      ]
    })

    return false
  }

  const handleRemovePendingReference = referenceId => {
    setPendingReferences(prev =>
      prev.filter(item => {
        const shouldKeep = item.id !== referenceId
        if (!shouldKeep && item.previewUrl && window.URL?.revokeObjectURL) {
          window.URL.revokeObjectURL(item.previewUrl)
        }
        return shouldKeep
      })
    )
  }

  const getPendingUploadMeta = item => ({
    id: item.id,
    filename: item.file?.name || 'Attached file',
    size: item.file?.size || 0,
    mime_type: item.file?.type || '',
    filetype: getReferenceKind(item.file),
    previewUrl: item.previewUrl || '',
    status: 'Uploading'
  })

  const handleSubmit = async () => {
    const message = draftMessage.trim()
    const referencesToUpload = pendingReferences
    if (
      !sessionId ||
      (!message && !referencesToUpload.length) ||
      isStreaming ||
      isSynthesizingVision ||
      isCreatingSession ||
      isUploadingReference ||
      isLockingVision ||
      Boolean(phaseBusyAction)
    ) {
      return
    }

    const localUploads = referencesToUpload.map(getPendingUploadMeta)

    setDraftMessage('')

    if (referencesToUpload.length) {
      const uploadedReferenceIds = referencesToUpload.map(item => item.id)
      setPendingReferences(prev =>
        prev.filter(item => !uploadedReferenceIds.includes(item.id))
      )
    }

    await onSendMessage(message || 'Uploaded reference.', {
      files: referencesToUpload.map(item => item.file),
      localUploads
    })
  }

  const handleKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
  }

  const handleStarterPrompt = item => {
    if (
      isStreaming ||
      isSynthesizingVision ||
      isCreatingSession ||
      isLockingVision ||
      Boolean(phaseBusyAction) ||
      !sessionId
    ) {
      return
    }

    if (item.action === 'start') {
      onStartPrompt()
      return
    }

    onSendMessage(item.message)
  }

  if (isCreatingSession) {
    return (
      <div
        className="creative-direction-chat__loading"
        role="status"
        aria-live="polite"
      >
        <div className="creative-direction-chat__loading-indicator">
          <Spin size="large" />
        </div>
        <div className="creative-direction-chat__loading-copy">
          <span>AI Director</span>
          <h2>Preparing your creative direction</h2>
          <p>
            Reading your story and setting up the creative workflow.
          </p>
        </div>
      </div>
    )
  }

  if (!sessionId && error) {
    return (
      <div className="creative-direction-chat__empty">
        <Icon type="warning" />
        <h3>Creative direction could not start</h3>
        <p>{getFriendlyErrorMessage(error)}</p>
        <Button type="primary" onClick={onRetryCreateSession}>
          Try again
        </Button>
      </div>
    )
  }

  const currentPhaseIndex = getPhaseIndex(phaseSteps, currentPhase)
  const visibleWorkflowPhases = phaseSteps.slice(1, currentPhaseIndex + 1)
  const hasConversation = messages.some(item =>
    String(item.content || '').trim()
  )
  const hasStreamingMessage = messages.some(item => item.streaming)
  const getMessagesForPhase = phase =>
    messages.filter(item => getMessagePhase(item) === phase)
  const latestAssistantMessage = [...messages]
    .reverse()
    .find(item => item.role === 'assistant' && !item.streaming)
  const latestReferenceRequestMessage = latestAssistantMessage?.metadata
    ?.referenceRequest
    ? latestAssistantMessage
    : null
  const shouldShowTypingForPhase = phase =>
    phase === currentPhase && isStreaming && !hasStreamingMessage
  const shouldShowVisionTimeline =
    hasConversation || Boolean(creativeStatement) || isSynthesizingVision
  const activeHeaderPhase = headerPhase || currentPhase
  const currentPhaseView = getPhaseView(activeHeaderPhase)
  const phaseHeaderLabel =
    hasConversation || activeHeaderPhase !== 'vision'
      ? currentPhaseView.label
      : 'AI Director'
  const phaseHeaderSubLabel =
    hasConversation || activeHeaderPhase !== 'vision'
      ? currentPhaseView.description
      : 'Creative direction conversation'
  const isChatTurnBusy =
    isStreaming ||
    isSynthesizingVision ||
    isUploadingReference ||
    isLockingVision
  const messageListReferenceProps = {
    activeReferenceMessageId: latestReferenceRequestMessage?.id || '',
    acceptedExtensions,
    phaseBusyAction,
    referenceUploadProgress,
    referenceUploadStatus,
    referenceUploadMessage,
    onUploadRequestedReference,
    onViewFinalizedReport: () => setIsFinalizedReportOpen(true)
  }

  const isPhaseGeneratingInChat = phaseKey => {
    const phasePayload = phaseData[phaseKey] || {}
    const hasGeneratedAssets = getGeneratedAssets(phaseData, phaseKey).length > 0
    const phaseActions = Array.isArray(phasePayload.lastAgentActions)
      ? phasePayload.lastAgentActions
      : []
    const phaseTaskStatus = String(
      phasePayload.generationTask?.status || ''
    ).toLowerCase()
    const liveStatus = String(generationStatus || '').toLowerCase()
    const hasGenerationStartedAction = phaseActions.some(
      isVisualGenerationAction
    )
    const hasActivePhaseTask =
      ACTIVE_CHAT_GENERATION_STATUSES.includes(phaseTaskStatus)
    const hasActiveLiveStatus =
      phaseKey === currentPhase &&
      ACTIVE_CHAT_GENERATION_STATUSES.includes(liveStatus)

    return (
      phaseBusyAction === `generate:${phaseKey}` ||
      (!hasGeneratedAssets && hasActivePhaseTask) ||
      (!hasGeneratedAssets && hasActiveLiveStatus) ||
      (!hasGeneratedAssets && hasGenerationStartedAction)
    )
  }

  const renderPhaseMarker = phaseKey => (
    <div
      className="creative-direction-chat__phase-marker"
      id={`creative-direction-phase-${phaseKey}`}
    >
      <span>{getPhaseView(phaseKey).label}</span>
    </div>
  )

  const getChatVisualSlotCount = phaseKey => {
    if (phaseKey === 'storyboard') return 1

    const phasePayload = phaseData[phaseKey] || {}
    const entityCount = Array.isArray(phasePayload.entities)
      ? phasePayload.entities.length
      : 0
    const fallbackCount = getPhaseCount(phaseData, phaseKey) || entityCount || 4

    return Math.max(4, Math.min(fallbackCount, 12))
  }

  const renderChatPhaseVisualStrip = (phaseKey, options = {}) => {
    const phasePayload = phaseData[phaseKey] || {}
    const assets = Array.isArray(options.assets)
      ? options.assets
      : getGeneratedAssets(phaseData, phaseKey)
    const isGenerating =
      typeof options.isGenerating === 'boolean'
        ? options.isGenerating
        : isPhaseGeneratingInChat(phaseKey)

    if (!assets.length && !isGenerating) return null

    const entityLookup = buildEntityLookup(
      phaseKey,
      Array.isArray(phasePayload.entities) ? phasePayload.entities : []
    )
    const visualAssets = assets.map((asset, index) =>
      buildVisualAssetViewModel(asset, index, entityLookup)
    )
    const visualAssetGroups = groupVisualAssetsByType(visualAssets)
    const previewImages = visualAssets
      .map(item =>
        item.fullImageUrl
          ? {
            src: item.fullImageUrl,
            alt: item.label
          }
          : null
      )
      .filter(Boolean)
    const expectedSlots = getChatVisualSlotCount(phaseKey)
    const placeholderCount = isGenerating
      ? Math.max(
        expectedSlots - assets.length,
        assets.length ? 0 : expectedSlots
      )
      : 0
    const headerLabel =
      phaseKey === 'storyboard'
        ? isGenerating
          ? 'Generating storyboard image...'
          : 'Generated storyboard image'
        : isGenerating
          ? 'Generating Visuals...'
          : 'Generated Visuals'
    const sparseAssetCount =
      !isGenerating && assets.length > 0 && assets.length <= 2
        ? assets.length
        : 0

    return (
      <section
        className={`creative-direction-chat__chat-artifact creative-direction-chat__chat-artifact--visuals${phaseKey === 'storyboard'
            ? ' creative-direction-chat__chat-artifact--single-visual'
            : ''
          }${sparseAssetCount
            ? ` creative-direction-chat__chat-artifact--asset-count-${sparseAssetCount}`
            : ''
          }`}
      >
        <div className="creative-direction-chat__chat-artifact-header">
          <span>{headerLabel}</span>
          {!isGenerating ? (
            <strong>{`${formatCount(assets.length)} ready`}</strong>
          ) : null}
        </div>

        <div className="creative-direction-chat__chat-asset-groups">
          {visualAssetGroups.map(group => (
            <div
              className="creative-direction-chat__chat-asset-group"
              key={group.key}
            >
              {phaseKey !== 'storyboard' ? (
                <div className="creative-direction-chat__chat-asset-group-label">
                  <span>{group.label}</span>
                </div>
              ) : null}
              <div className="creative-direction-chat__chat-asset-strip">
                {group.items.map(item => {
                  const initialIndex = previewImages.findIndex(
                    image => image.src === item.fullImageUrl
                  )

                  return (
                    <figure
                      className="creative-direction-chat__chat-asset"
                      key={
                        item.asset?.id ||
                        item.fullImageUrl ||
                        item.thumbnailUrl ||
                        `${phaseKey}-${item.index}`
                      }
                    >
                      {item.thumbnailUrl || item.fullImageUrl ? (
                        <ImagePreview
                          src={item.thumbnailUrl || item.fullImageUrl}
                          alt={item.label}
                          images={previewImages}
                          initialIndex={initialIndex >= 0 ? initialIndex : 0}
                          cancelOnUnmount
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain'
                          }}
                        />
                      ) : (
                        <span>
                          <Icon type="picture" />
                        </span>
                      )}
                      {item.hasEntityName ? (
                        <figcaption className="creative-direction-chat__chat-asset-meta">
                          <strong title={item.entityName}>
                            {item.entityName}
                          </strong>
                        </figcaption>
                      ) : null}
                    </figure>
                  )
                })}
              </div>
            </div>
          ))}
          {placeholderCount > 0 ? (
            <div className="creative-direction-chat__chat-asset-group creative-direction-chat__chat-asset-group--loading">
              <div className="creative-direction-chat__chat-asset-strip">
                {Array.from({ length: placeholderCount }).map((_, index) => (
                  <figure
                    className="creative-direction-chat__chat-asset creative-direction-chat__chat-asset--loading"
                    key={`${phaseKey}-loading-${index}`}
                  >
                    <span aria-label="Generating visual preview" />
                  </figure>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </section>
    )
  }

  const getMessageActions = message => {
    const metadataActions = Array.isArray(message?.metadata?.actions)
      ? message.metadata.actions
      : []
    if (metadataActions.length) return metadataActions
    return Array.isArray(message?.actions) ? message.actions : []
  }

  const hasVisualGenerationAction = message =>
    getMessageActions(message).some(isVisualGenerationAction)

  const hasFinalReportAction = message =>
    getMessageActions(message).some(action => {
      const actionName = getMessageActionName(action)
      const result =
        getMessageActionResult(action) || {}

      return (
        actionName.includes('finalize') ||
        actionName.includes('guideline') ||
        actionName.includes('report') ||
        Boolean(result.creative_guideline || result.creativeGuideline || result.bible)
      )
    })

  const hasStoryboardPlanAction = message =>
    getMessageActions(message).some(action => {
      const actionName = getMessageActionName(action)
      const result = getMessageActionResult(action)
      const input =
        action?.input || action?.params || action?.arguments || {}
      const isPlanAction = [
        'propose_plan',
        'propose_storyboard_plan',
        'generate_storyboard_plan',
        'update_plan',
        'plan_updated',
        'storyboard_plan_updated',
        'approve_plan'
      ].includes(actionName)
      const hasPlanData = Boolean(
        result.plan ||
        result.storyboard_plan ||
        result.storyboardPlan ||
        result.shot_plan ||
        result.shotPlan ||
        input.plan ||
        input.storyboard_plan ||
        input.storyboardPlan ||
        Array.isArray(result.shots) ||
        Array.isArray(result.panels)
      )

      return isPlanAction && hasPlanData
    })

  const hasFinalReportEvent = messages.some(hasFinalReportAction)
  const hasStoryboardPlanEvent = messages.some(hasStoryboardPlanAction)

  const getInlineVisualProps = (phaseKey, phaseMessages) => {
    const generationMessages = phaseMessages.filter(
      message =>
        message?.role === 'assistant' && hasVisualGenerationAction(message)
    )

    if (!generationMessages.length) {
      return {
        inlineVisualProps: {},
        hasInlineVisual: false
      }
    }

    const latestGenerationMessage =
      generationMessages[generationMessages.length - 1]
    const livePhaseAssets = getGeneratedAssets(phaseData, phaseKey)
    const isLiveGeneration = isPhaseGeneratingInChat(phaseKey)
    const inlineArtifactsByMessageId = generationMessages.reduce(
      (artifacts, message) => {
        const artifact = message.metadata?.generationArtifact
        const isLatest = message.id === latestGenerationMessage.id
        const hasSnapshot = Array.isArray(artifact?.assets)
        const isGenerating = artifact
          ? ACTIVE_CHAT_GENERATION_STATUSES.includes(
            String(artifact.status || '').toLowerCase()
          )
          : isLatest && isLiveGeneration
        const assets = hasSnapshot
          ? artifact.assets
          : isLatest && !isLiveGeneration
            ? livePhaseAssets
            : []
        const visualStrip = renderChatPhaseVisualStrip(phaseKey, {
          assets,
          isGenerating
        })

        if (visualStrip) {
          artifacts[message.id] = visualStrip
        }

        return artifacts
      },
      {}
    )

    return {
      inlineVisualProps: {
        inlineArtifactsByMessageId
      },
      hasInlineVisual: Boolean(
        Object.keys(inlineArtifactsByMessageId).length
      )
    }
  }

  const renderChatAdvanceStatus = () => {
    if (!String(phaseBusyAction || '').startsWith('advance:')) return null

    const sourcePhase = String(phaseBusyAction).replace('advance:', '')
    const sourceLabel = getPhaseView(sourcePhase).label
    const nextLabel = getNextPhaseLabel(phaseSteps, sourcePhase)

    return (
      <section className="creative-direction-chat__chat-artifact creative-direction-chat__chat-artifact--status">
        <div className="creative-direction-chat__chat-artifact-header">
          <Icon type="loading" />
          <span>{`Moving to ${nextLabel}...`}</span>
          <strong>Saving</strong>
        </div>
        <p className="creative-direction-chat__chat-artifact-text">
          {`${sourceLabel} is being saved and ${nextLabel} is being prepared.`}
        </p>
      </section>
    )
  }

  const renderChatStoryboardPlan = () => {
    const storyboardData = phaseData.storyboard || {}
    const planCount = getStoryboardPlanCount(storyboardData.plan)
    const hasPlan = planCount > 0

    if (!hasPlan || hasStoryboardPlanEvent) return null

    return (
      <section className="creative-direction-chat__chat-artifact creative-direction-chat__chat-artifact--storyboard-plan creative-direction-chat__drawer-doc-section">
        <div className="creative-direction-chat__drawer-doc-title">
          <Icon type="profile" />
          <span>Storyboard Plan</span>
          {hasPlan ? (
            <span className="creative-direction-chat__drawer-doc-count">
              {formatCount(planCount)}
            </span>
          ) : null}
        </div>
        <div className="creative-direction-chat__chat-storyboard-plan-body">
          <StoryboardPlanPreview
            plan={storyboardData.plan}
            committedPanels={storyboardData.committedPanels}
            planApproved={Boolean(
              storyboardData.planApproved ||
              storyboardData.committedPanels ||
              phaseData.status?.storyboardPlanApproved
            )}
            previewLimit={3}
            showMoreControl
          />
        </div>
      </section>
    )
  }

  const renderChatFinalReport = () => {
    const hasFinalizedReport = Boolean(isFinalized && creativeGuideline)

    if (hasFinalizedReport && hasFinalReportEvent) return null

    return (
      <section className="creative-direction-chat__event-card creative-direction-chat__event-card--report creative-direction-chat__final-report-card">
        <div className="creative-direction-chat__event-card-header">
          <div>
            <span>Creative guideline</span>
            <strong>
              {hasFinalizedReport
                ? 'Final report is ready'
                : 'Ready to create your final report'}
            </strong>
          </div>
          <Icon type={hasFinalizedReport ? 'file-done' : 'profile'} />
        </div>
        {!hasFinalizedReport ? (
          <p>
            Turn the completed creative direction into a reviewable report.
          </p>
        ) : null}
        <button
          type="button"
          className="creative-direction-chat__event-toggle"
          disabled={
            !hasFinalizedReport &&
            (phaseBusyAction === 'finalize' ||
              !onFinalizeCreativeDirection)
          }
          onClick={
            hasFinalizedReport
              ? () => setIsFinalizedReportOpen(true)
              : onFinalizeCreativeDirection
          }
        >
          {hasFinalizedReport
            ? 'View final report'
            : phaseBusyAction === 'finalize'
              ? 'Generating report...'
              : 'Generate final report'}
          <Icon
            type={
              phaseBusyAction === 'finalize' && !hasFinalizedReport
                ? 'loading'
                : hasFinalizedReport
                  ? 'file-done'
                  : 'arrow-right'
            }
          />
        </button>
      </section>
    )
  }

  const renderPhaseTimeline = phaseKey => {
    const phaseMessages = getMessagesForPhase(phaseKey)
    const showTyping = shouldShowTypingForPhase(phaseKey)
    const visualStrip = renderChatPhaseVisualStrip(phaseKey)
    const { inlineVisualProps, hasInlineVisual } = getInlineVisualProps(
      phaseKey,
      phaseMessages
    )

    if (!phaseMessages.length && !showTyping) return visualStrip

    return (
      <>
        <MessageList
          messages={phaseMessages}
          showTyping={showTyping}
          {...messageListReferenceProps}
          {...inlineVisualProps}
        />
        {!hasInlineVisual ? visualStrip : null}
      </>
    )
  }

  const renderStoryboardPhaseTimeline = phaseKey => {
    const phaseMessages = getMessagesForPhase(phaseKey)
    const storyboardPlan = renderChatStoryboardPlan()
    const visualStrip = renderChatPhaseVisualStrip(phaseKey)
    const showTyping = shouldShowTypingForPhase(phaseKey)
    const hasPlan = getStoryboardPlanCount(phaseData.storyboard?.plan) > 0
    const { inlineVisualProps, hasInlineVisual } = getInlineVisualProps(
      phaseKey,
      phaseMessages
    )

    if (!storyboardPlan) {
      return (
        <>
          <MessageList
            messages={phaseMessages}
            showTyping={showTyping}
            {...messageListReferenceProps}
            {...inlineVisualProps}
          />
          {!hasInlineVisual ? visualStrip : null}
        </>
      )
    }

    if (!hasPlan) {
      return (
        <>
          <MessageList
            messages={phaseMessages}
            showTyping={showTyping}
            {...messageListReferenceProps}
            {...inlineVisualProps}
          />
          {storyboardPlan}
          {!hasInlineVisual ? visualStrip : null}
        </>
      )
    }

    const anchorIndex = phaseMessages.findIndex(
      message => message?.role === 'assistant' && !message?.streaming
    )

    if (anchorIndex < 0) {
      return (
        <>
          {storyboardPlan}
          <MessageList
            messages={phaseMessages}
            showTyping={showTyping}
            {...messageListReferenceProps}
            {...inlineVisualProps}
          />
          {!hasInlineVisual ? visualStrip : null}
        </>
      )
    }

    return (
      <>
        <MessageList
          messages={phaseMessages.slice(0, anchorIndex + 1)}
          showTyping={false}
          {...messageListReferenceProps}
          {...inlineVisualProps}
        />
        {storyboardPlan}
        <MessageList
          messages={phaseMessages.slice(anchorIndex + 1)}
          showTyping={showTyping}
          {...messageListReferenceProps}
          {...inlineVisualProps}
        />
        {!hasInlineVisual ? visualStrip : null}
      </>
    )
  }

  const renderVisionTimeline = ({ isArchived = false } = {}) => (
    <>
      <MessageList
        messages={getMessagesForPhase('vision')}
        showTyping={!isArchived && shouldShowTypingForPhase('vision')}
        {...messageListReferenceProps}
      />
      <VisionDirectionCard
        creativeStatement={creativeStatement}
        hasConversation={hasConversation}
        isStreaming={isStreaming}
        isSynthesizingVision={isSynthesizingVision}
        isLockingVision={isLockingVision}
        isVisionLocked={isVisionLocked}
        isArchived={isArchived}
        onSynthesizeVision={onSynthesizeVision}
        onLockVision={onLockVision}
      />
    </>
  )

  const timelinePanel =
    isVisionPhase && !shouldShowVisionTimeline ? null : isVisionPhase ? (
      renderVisionTimeline()
    ) : (
      <>
        {shouldShowVisionTimeline ? (
          <React.Fragment key="vision">
            {renderPhaseMarker('vision')}
            {renderVisionTimeline({ isArchived: true })}
          </React.Fragment>
        ) : null}
        {visibleWorkflowPhases.map(phaseStep => {
          const phaseKey = phaseStep.key

          return (
            <React.Fragment key={phaseKey}>
              {renderPhaseMarker(phaseKey)}
              {phaseKey === 'storyboard' ? (
                renderStoryboardPhaseTimeline(phaseKey)
              ) : phaseKey === 'finalized' ? (
                <>
                  {renderPhaseTimeline(phaseKey)}
                  {renderChatFinalReport()}
                </>
              ) : (
                renderPhaseTimeline(phaseKey)
              )}
            </React.Fragment>
          )
        })}
        {renderChatAdvanceStatus()}
      </>
    )

  return (
    <div
      className={`creative-direction-chat${isProgressPanelCollapsed
          ? ' creative-direction-chat--process-collapsed'
          : ''
        }`}
    >
      <ProgressPreview
        phaseSteps={phaseSteps}
        phaseProgress={phaseProgress}
        currentPhase={currentPhase}
        expandedPhases={{}}
        progressPercent={progressPercent}
        isFinalized={isFinalized}
        onPhaseSelect={handlePhaseSelect}
        isCollapsed={isProgressPanelCollapsed}
        onToggleCollapse={handleToggleProgressPanel}
      />

      <main className="creative-direction-chat__main">
        <div className="creative-direction-chat__workspace">
          <ConversationCard
            sessionId={sessionId}
            messages={messages}
            timelinePanel={timelinePanel}
            scrollRef={scrollRef}
            scrollAnchorRef={scrollAnchorRef}
            errorMessage={
              error && sessionId ? getFriendlyErrorMessage(error) : ''
            }
            isCreatingSession={isCreatingSession}
            isStreaming={isStreaming}
            isSynthesizingVision={isSynthesizingVision}
            isUploadingReference={isUploadingReference}
            isGenerationPolling={isGenerationPolling}
            isLockingVision={isLockingVision}
            phaseBusyAction={phaseBusyAction}
            isActiveWorkspaceCollapsed={false}
            acceptedExtensions={acceptedExtensions}
            pendingReferences={pendingReferences}
            activeWorkspace={null}
            draftMessage={draftMessage}
            isArtifactsOpen={false}
            onDraftMessageChange={setDraftMessage}
            onAttachReference={handleAttachReference}
            onRemovePendingReference={handleRemovePendingReference}
            onSubmit={handleSubmit}
            onStopAgentTurn={onStopAgentTurn}
            onKeyDown={handleKeyDown}
            onStarterPrompt={handleStarterPrompt}
            activePhaseLabel={phaseHeaderLabel}
            activePhaseSubLabel={phaseHeaderSubLabel}
            progressPercent={progressPercent}
            isProgressPanelCollapsed={isProgressPanelCollapsed}
            onToggleProgressPanel={handleToggleProgressPanel}
          />
        </div>
      </main>

      <FinalizedReportModal
        visible={isFinalizedReportOpen}
        report={creativeGuideline}
        onClose={() => setIsFinalizedReportOpen(false)}
      />
    </div>
  )
}

export default CreativeDirectionChat
